/**
 * E-Invoice View/Print Page
 * 
 * Displays E-Invoice in print-ready format
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EInvoicePrintLayout } from "./EInvoicePrintLayout";
import axios from "axios";
import { format } from "date-fns";
import { GlobalService } from "../service/GlobalService";
import SideBar from "../SideBar";
import Header from "../header";

// Simple JWT decode function (base64 decode)
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export default function EInvoiceViewPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [printData, setPrintData] = useState(null);
  const [error, setError] = useState(null);

  const einvoiceId = params.id; // IRN or document ID

  useEffect(() => {
    loadEInvoiceData();
    
    // Force re-initialization of navigation scripts after navigation
    // This ensures SideBar and other navigation elements work properly
    const timer = setTimeout(() => {
      // Trigger any necessary re-initializations
      if (window.jQuery) {
        // If jQuery is available, re-initialize any plugins
        window.jQuery(document).trigger('ready');
      }
      
      // Dispatch a custom event to re-initialize navigation
      window.dispatchEvent(new Event('resize'));
      
      // Force sidebar menu to re-initialize if needed
      const sidebarToggle = document.getElementById('sidebar-toggle-button-close');
      if (sidebarToggle) {
        // Sidebar is already rendered, just ensure it's functional
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [einvoiceId]);

  // Authenticate with GST API
  const authenticateGST = async () => {
    try {
      const email = "shubhamkurade63@gmail.com";
      const HEADERTOK = {
        headers: {
          "username": "API_29FTHPK8890K1ZN",
          "password": "Sangram#98",
          "ip_address": "192.168.1.6",
          "client_id": "EINP4913288e-348b-41e8-a476-ad6c8e0fb94a",
          "client_secret": "EINP81dd4f2f-c3a6-45ec-93d1-bb948ce22b04",
          "gstin": "29FTHPK8890K1ZN"
        }
      };

      const response = await axios.get(
        'https://solitary-lab-966b.sparklabit.workers.dev/einvoice/authenticate?email=' + email,
        HEADERTOK
      );

      if (response.data.status_cd === "Sucess" || response.data.status_cd === "1") {
        // Store authentication data in sessionStorage
        sessionStorage.setItem('AuthenticationData', JSON.stringify(response.data));
        return response.data;
      } else {
        throw new Error(response.data.status_desc || "Authentication failed");
      }
    } catch (error) {
      console.error("Authentication Error:", error);
      throw new Error("Failed to authenticate. Please try again.");
    }
  };

  const loadEInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Ensure authentication data exists
      let authData = sessionStorage.getItem("AuthenticationData");
      if (!authData) {
        // Authenticate if not available
        try {
          await authenticateGST();
          authData = sessionStorage.getItem("AuthenticationData");
        } catch (authError) {
          setError("Authentication failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Step 2: Try to get from sessionStorage first (if IRN matches)
      const storedData = sessionStorage.getItem("einvoiceData");
      if (storedData) {
        try {
          const einvoiceData = JSON.parse(storedData);
          // Check if stored data matches the requested IRN
          const storedIRN = einvoiceData.data?.Irn || einvoiceData.Irn || "";
          if (storedIRN === einvoiceId || einvoiceId === "") {
            const printData = convertToPrintData(einvoiceData);
            setPrintData(printData);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Error parsing stored data:", e);
        }
      }

      // Step 3: If not in storage or doesn't match, fetch from backend database
      try {
        const response = await fetch(`${GlobalService.path}/e-invoice`);
        if (response.ok) {
          const data = await response.json();
          const invoice = data.data?.find(
            (item) => item.irn === einvoiceId || item.document_no === einvoiceId
          );
          
          if (invoice && invoice.irn) {
            // Fetch E-Invoice details by IRN
            await fetchEInvoiceByIRN(invoice.irn);
          } else {
            setError("E-Invoice not found in database. Please check the IRN or Document No.");
            setLoading(false);
          }
        } else {
          setError("Failed to fetch E-Invoice data from database");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching E-Invoice:", err);
        setError("Failed to load E-Invoice data. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading E-Invoice:", error);
      setError(error.message || "Failed to load E-Invoice");
      setLoading(false);
    }
  };

  const fetchEInvoiceByIRN = async (irn, retryCount = 0) => {
    try {
      // Get authentication data
      let authData = sessionStorage.getItem("AuthenticationData");
      if (!authData) {
        // Try to authenticate if not available
        try {
          await authenticateGST();
          authData = sessionStorage.getItem("AuthenticationData");
        } catch (authError) {
          setError("Authentication failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      const authenticationData = JSON.parse(authData);
      const email = "shubhamkurade63@gmail.com";
      
      const HEADER = {
        headers: {
          username: authenticationData.data?.UserName || authenticationData.data?.username || "API_29FTHPK8890K1ZN",
          ip_address: authenticationData.header?.ip_address || "192.168.1.6",
          client_id: authenticationData.header?.client_id || "EINP4913288e-348b-41e8-a476-ad6c8e0fb94a",
          client_secret: authenticationData.header?.client_secret || "EINP81dd4f2f-c3a6-45ec-93d1-bb948ce22b04",
          gstin: authenticationData.header?.gstin || "29FTHPK8890K1ZN",
          "auth-token": authenticationData.data?.AuthToken || authenticationData.data?.authToken,
        },
      };

      const response = await axios.get(
        "https://solitary-lab-966b.sparklabit.workers.dev/einvoice/type/GETIRN/version/V1_03",
        {
          params: {
            param1: irn,
            email: email,
            supplier_gstn: authenticationData.header?.gstin || "29FTHPK8890K1ZN",
          },
          headers: HEADER.headers,
        }
      );

      if (response.data && (response.data.status_cd === "1" || response.data.status_cd === "Sucess")) {
        const printData = convertToPrintData(response.data);
        setPrintData(printData);
        sessionStorage.setItem("einvoiceData", JSON.stringify(response.data));
      } else {
        setError(response.data?.status_desc || "Failed to fetch E-Invoice from API");
      }
    } catch (error) {
      console.error("Error fetching E-Invoice by IRN:", error);
      if ((error.response?.status === 401 || error.response?.status === 403) && retryCount < 1) {
        // Token might be expired, try to re-authenticate (only once)
        try {
          await authenticateGST();
          // Retry fetching with incremented retry count
          await fetchEInvoiceByIRN(irn, retryCount + 1);
          return;
        } catch (authError) {
          setError("Authentication failed. Please try again.");
        }
      } else {
        setError(error.response?.data?.status_desc || error.message || "Failed to fetch E-Invoice details");
      }
    } finally {
      setLoading(false);
    }
  };

  const convertToPrintData = (einvoiceData) => {
    const responseData = einvoiceData.data || einvoiceData || {};
    
    // Parse SignedInvoice
    let invoiceData = {};
    const signedInvoiceStr = responseData.SignedInvoice || responseData.signedInvoice;
    
    if (signedInvoiceStr) {
      try {
        let parsedData = {};
        if (typeof signedInvoiceStr === 'string' && signedInvoiceStr.startsWith("eyJ")) {
          // It's a JWT token
          const decoded = decodeJWT(signedInvoiceStr);
          if (decoded) {
            if (decoded.data && typeof decoded.data === 'string') {
              parsedData = JSON.parse(decoded.data);
            } else if (decoded.data && typeof decoded.data === 'object') {
              parsedData = decoded.data;
            } else {
              parsedData = decoded;
            }
          }
        } else {
          parsedData = typeof signedInvoiceStr === 'string' ? JSON.parse(signedInvoiceStr) : signedInvoiceStr;
        }
        invoiceData = parsedData.data || parsedData;
      } catch (error) {
        console.error("Failed to parse SignedInvoice:", error);
      }
    }

    const data = (invoiceData.DocDtls || invoiceData.SellerDtls || invoiceData.BuyerDtls || invoiceData.ItemList || invoiceData.ValDtls) 
      ? invoiceData 
      : responseData;

    const docDetails = data.DocDtls || data.docDtls || {};
    const tranDetails = data.TranDtls || data.tranDtls || {};
    const sellerDetails = data.SellerDtls || data.sellerDtls || {};
    const buyerDetails = data.BuyerDtls || data.buyerDtls || {};
    const shipDetails = data.ShipDtls || data.shipDtls || {};
    const itemList = data.ItemList || data.itemList || [];
    const valDetails = data.ValDtls || data.valDtls || {};

    // Format dates
    const formatDateTime = (dateStr) => {
      if (!dateStr) return "";
      try {
        // Handle different date formats
        if (dateStr.includes("/")) {
          const [day, month, year, ...timeParts] = dateStr.split(/[/\s:]/);
          if (timeParts.length > 0) {
            return `${day}-${month}-${year} ${timeParts.join(':')}`;
          }
          return `${day}-${month}-${year}`;
        }
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return format(date, "dd-MM-yyyy HH:mm:ss");
      } catch {
        return dateStr;
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      try {
        if (dateStr.includes("/")) {
          const [day, month, year] = dateStr.split("/");
          return `${day}-${month}-${year}`;
        }
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return format(date, "dd-MM-yyyy");
      } catch {
        return dateStr;
      }
    };

    // Get state name
    const getStateName = (code) => {
      if (!code) return "";
      const stateMap = {
        "29": "KARNATAKA", "27": "MAHARASHTRA", "09": "UTTAR PRADESH", "10": "BIHAR",
        "24": "GUJARAT", "06": "HARYANA", "02": "HIMACHAL PRADESH", "20": "JHARKHAND",
        "32": "KERALA", "23": "MADHYA PRADESH", "21": "ODISHA", "08": "RAJASTHAN",
        "33": "TAMIL NADU", "36": "TELANGANA", "19": "WEST BENGAL", "07": "DELHI",
        "01": "JAMMU AND KASHMIR", "03": "PUNJAB", "04": "CHANDIGARH", "05": "UTTARAKHAND",
        "11": "SIKKIM", "12": "ARUNACHAL PRADESH", "13": "NAGALAND", "14": "MANIPUR",
        "15": "MIZORAM", "16": "TRIPURA", "17": "MEGHALAYA", "18": "ASSAM",
        "22": "CHHATTISGARH", "25": "DAMAN AND DIU", "26": "DADRA AND NAGAR HAVELI",
        "28": "ANDHRA PRADESH", "30": "GOA", "31": "LAKSHADWEEP", "34": "PUDUCHERRY",
        "35": "ANDAMAN AND NICOBAR ISLANDS", "37": "LADAKH"
      };
      return stateMap[code] || code;
    };

    // QR Code
    const qrCodeRaw = responseData.SignedQRCode || responseData.signedQRCode || "";
    let qrCodeBase64 = "";
    if (qrCodeRaw) {
      if (qrCodeRaw.startsWith("data:image")) {
        qrCodeBase64 = qrCodeRaw;
      } else if (qrCodeRaw.startsWith("http://") || qrCodeRaw.startsWith("https://")) {
        qrCodeBase64 = qrCodeRaw;
      } else if (qrCodeRaw.startsWith("eyJ")) {
        qrCodeBase64 = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeRaw)}`;
      } else {
        qrCodeBase64 = `data:image/png;base64,${qrCodeRaw}`;
      }
    }

    return {
      supplierGSTIN: sellerDetails.Gstin || sellerDetails.gstin || "",
      supplierName: sellerDetails.TrdNm || sellerDetails.trdNm || "",
      supplierAddress: {
        line1: sellerDetails.Addr1 || sellerDetails.addr1 || "",
        line2: sellerDetails.Addr2 || sellerDetails.addr2,
        city: sellerDetails.Loc || sellerDetails.loc || "",
        district: "",
        pin: sellerDetails.Pin || sellerDetails.pin || "",
        state: getStateName(sellerDetails.Stcd || sellerDetails.stcd || ""),
      },
      recipientGSTIN: buyerDetails.Gstin || buyerDetails.gstin || "",
      recipientName: buyerDetails.TrdNm || buyerDetails.trdNm || "",
      recipientAddress: {
        line1: buyerDetails.Addr1 || buyerDetails.addr1 || "",
        line2: buyerDetails.Addr2 || buyerDetails.addr2,
        city: buyerDetails.Loc || buyerDetails.loc || "",
        pin: buyerDetails.Pin || buyerDetails.pin || "",
        state: getStateName(buyerDetails.Stcd || buyerDetails.stcd || ""),
        placeOfSupply: getStateName(buyerDetails.Pos || buyerDetails.pos || ""),
      },
      shipToGSTIN: shipDetails.Gstin || shipDetails.gstin,
      shipToName: shipDetails.TrdNm || shipDetails.trdNm,
      shipToAddress: shipDetails.Gstin ? {
        line1: shipDetails.Addr1 || shipDetails.addr1 || "",
        line2: shipDetails.Addr2 || shipDetails.addr2,
        city: shipDetails.Loc || shipDetails.loc || "",
        pin: shipDetails.Pin || shipDetails.pin || "",
        state: getStateName(shipDetails.Stcd || shipDetails.stcd || ""),
      } : undefined,
      irn: responseData.Irn || responseData.irn || "",
      ackNo: responseData.AckNo || responseData.ackNo || "",
      ackDateTime: formatDateTime(responseData.AckDt || responseData.ackDt || ""),
      supplyTypeCode: tranDetails.SupTyp || tranDetails.supTyp || "",
      documentNo: docDetails.No || docDetails.no || "",
      documentType: docDetails.Typ === "INV" ? "Tax Invoice" : docDetails.Typ || docDetails.typ || "",
      documentDate: formatDate(docDetails.Dt || docDetails.dt || ""),
      placeOfSupply: getStateName(buyerDetails.Pos || buyerDetails.pos || ""),
      items: itemList.map((item, index) => {
        const taxRate = parseFloat(item.GstRt || item.gstRt || 0);
        const cgstRate = parseFloat(item.CgstRt || item.cgstRt || 0);
        const sgstRate = parseFloat(item.SgstRt || item.sgstRt || 0);
        const igstRate = parseFloat(item.IgstRt || item.igstRt || 0);
        
        // Format tax rate as per PDF: "18.00+0.00|0.00+0"
        let taxRateText = "0.00+0.00|0.00+0";
        if (cgstRate > 0 && sgstRate > 0) {
          taxRateText = `${taxRate.toFixed(2)}+0.00|0.00+0`;
        } else if (igstRate > 0) {
          taxRateText = `0.00+0.00|${igstRate.toFixed(2)}+0`;
        } else if (taxRate > 0) {
          taxRateText = `${taxRate.toFixed(2)}+0.00|0.00+0`;
        }

        return {
          slNo: item.SlNo || item.slNo || index + 1,
          description: item.PrdNm || item.prdNm || item.PrdDesc || item.prdDesc || "",
          hsn: item.HsnCd || item.hsnCd || "",
          qty: parseFloat(item.Qty || item.qty || 0),
          unit: item.Unit || item.unit || "",
          unitPrice: parseFloat(item.UnitPrice || item.unitPrice || 0),
          discount: parseFloat(item.Discount || item.discount || 0),
          taxable: parseFloat(item.AssAmt || item.assAmt || 0),
          taxRateText: taxRateText,
          otherCharges: parseFloat(item.OtherCharges || item.otherCharges || 0),
          total: parseFloat(item.TotItemVal || item.totItemVal || 0),
        };
      }),
      summary: {
        taxableAmt: parseFloat(valDetails.AssVal || valDetails.assVal || 0),
        cgstAmt: parseFloat(valDetails.CgstVal || valDetails.cgstVal || 0),
        sgstAmt: parseFloat(valDetails.SgstVal || valDetails.sgstVal || 0),
        igstAmt: parseFloat(valDetails.IgstVal || valDetails.igstVal || 0),
        cessAmt: parseFloat(valDetails.CessVal || valDetails.cessVal || 0),
        stateCessAmt: 0, // Not in standard response
        discount: 0, // Calculate if needed
        otherCharges: 0, // Calculate if needed
        roundOff: 0, // Calculate if needed
        totalInvAmt: parseFloat(valDetails.TotInvVal || valDetails.totInvVal || 0),
      },
      ewaybill: responseData.EwbNo ? {
        ewayBillNo: responseData.EwbNo || "",
        ewayBillDate: formatDate(responseData.EwbDt || responseData.ewbDt || ""),
        validTill: formatDate(responseData.EwbValidTill || responseData.ewbValidTill || ""),
      } : undefined,
      generatedBy: sellerDetails.Gstin || sellerDetails.gstin || "",
      printDateTime: new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      signedByText: "Digitally Signed by NIC-IRP",
      signedOnDateTime: formatDateTime(responseData.AckDt || responseData.ackDt || ""),
      signedQRCodeBase64: qrCodeBase64 || undefined,
    };
  };

  if (loading) {
    return (
      <div className="page-container">
        <SideBar />
        <div className="page-content">
          <Header />
          <div className="panel panel-white" style={{ padding: "40px", textAlign: "center" }}>
            <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite" }}>
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <p style={{ marginTop: "20px" }}>Loading E-Invoice...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <SideBar />
        <div className="page-content">
          <Header />
          <div className="panel panel-white" style={{ padding: "40px", textAlign: "center" }}>
            <h2 className="text-danger">E-Invoice Not Found</h2>
            <p className="text-muted">{error}</p>
            <button className="btn btn-primary" onClick={() => navigate("/IrnList")}>
              Back to IRN List
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!printData) {
    return (
      <div className="page-container">
        <SideBar />
        <div className="page-content">
          <Header />
          <div className="panel panel-white" style={{ padding: "40px", textAlign: "center" }}>
            <h2 className="text-danger">E-Invoice Not Found</h2>
            <p className="text-muted">Unable to load E-Invoice data</p>
            <button className="btn btn-primary" onClick={() => navigate("/IrnList")}>
              Back to IRN List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SideBar />
      <div className="page-content">
        <Header />
        <div className="panel panel-white" style={{ padding: "20px" }}>
          <EInvoicePrintLayout data={printData} />
        </div>
      </div>
    </div>
  );
}
